import { Component, OnInit, AfterViewInit, ViewEncapsulation, forwardRef, Input, Output, EventEmitter, ElementRef, PLATFORM_ID, Inject, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import Quill from 'quill';
import katex from 'katex';
import { MathFieldBlot } from './math-field.blot'
import { attachMathfieldKeyboardListeners, closeVirtualKeyboard } from './math-field-keyboard';

// MathFieldBlot.blotName = 'input';
// MathFieldBlot.tagName = 'span'; 

@Component({
  selector: 'app-quill-editor',
  templateUrl: './quill-editor.component.html',
  styleUrls: ['./quill-editor.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => QuillEditorComponent),
    multi: true
  }],
  encapsulation: ViewEncapsulation.None
})
export class QuillEditorComponent implements ControlValueAccessor, AfterViewInit {
  @Input() placeholder: string = 'Compose here...';
  @Input() height: string = '400px';
  @Input() maxHeight: string | null | undefined;
  @Input() readonly: boolean = false;
  @Output() contentChange = new EventEmitter<string>();


  quill: Quill;
  _mathVirtualKeyboard: any;
  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };
  private contentSnapshot: string = '';
  private textChangeHandler: () => void;
  private keyboardOpen: boolean = false;
  private pageContainerDefaultHeight: string = '';

  constructor(
    private elementRef: ElementRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {
    // Register the custom blot before initializing Quill
    if (!Quill.import['blots/embed']) {
      Quill.register(MathFieldBlot);
    }
  }

  ngAfterViewInit(): void {
    this.initializeEditor();
    this._mathVirtualKeyboard = (window as any).mathVirtualKeyboard;

    this.updateMathFieldIcon();
    this.patchTooltipPositioning();

    // this.updateMathFieldKeyboard();
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    if (this.quill) {
      this.quill.off('text-change', this.textChangeHandler);
    }
  }

  private initializeEditor(): void {

    (window as any).Quill = Quill;
    (window as any).katex = katex;

    // Ensure Quill is available globally
    if (typeof Quill === 'undefined') {
      throw new Error('Quill.js not loaded. Check angular.json scripts array.');
    }

    const toolbarOptions = {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code'],
        ['link', 'image', 'video'],
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ script: 'sub' }, { script: 'super' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        ['clean'],
        // ['formula'],
        ['mathField']
      ],
      handlers: {
        'mathField': () => this.insertMathField()
      }
    };

    const editorElement = this.elementRef.nativeElement.querySelector('#editor');
    editorElement.style.height = this.height;
    if (this.maxHeight != null) {
      editorElement.style.maxHeight = this.maxHeight;
    }

    // Initialize Quill
    this.quill = new Quill(editorElement, {
      modules: {
        formula: true,
        toolbar: toolbarOptions
      },
      theme: 'snow',
      placeholder: this.readonly ? '' : this.placeholder
    });

    // Set initial content if available
    if (this.contentSnapshot) {
      this.quill.root.innerHTML = this.contentSnapshot;

      // Initialize any math fields that were loaded from the server
      this.initializeMathFields();
    }

    // Set up text change handler
    this.textChangeHandler = () => {
      const html = this.quill.root.innerHTML;
      // Avoid triggering onChange during programmatic updates
      if (html !== this.contentSnapshot) {
        this.contentSnapshot = html;
        this.onChange(html);
        this.onTouched();
        this.contentChange.emit(html);
      }
    };

    this.quill.on('text-change', this.textChangeHandler);

    if (this.readonly) {
      this.setDisabledState(this.readonly);
    }
  }

  // ControlValueAccessor methods
  writeValue(value: string): void {
    this.contentSnapshot = this.sanitizeHTML(value) || '';
    if (this.quill) {
      if (this.quill.root.innerHTML !== this.contentSnapshot) {
        this.quill.root.innerHTML = this.contentSnapshot;
        this.initializeMathFields();
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.quill) {
      this.quill.enable(!isDisabled);
    }
  }

  private initializeMathFields(): void {
    // Find all math-field elements in the editor
    this.pageContainerDefaultHeight = (document.querySelector('.page-container') as HTMLElement).style.height;
    const mathFields: any = this.quill.root.querySelectorAll('math-field');

    mathFields.forEach(mathField => {
      // Add input event listener to ensure content changes trigger Quill updates
      mathField.addEventListener('input', () => {
        // Get the closest Quill editor instance
        const quill = Quill.find(mathField.closest('.ql-editor'));
        if (quill) {
          // Force an update to ensure changes are registered
          quill.update('user');
          if (this.textChangeHandler) {
            this.textChangeHandler();
          }
        }
        mathField.innerHTML = mathField.value;
      });

      attachMathfieldKeyboardListeners({
        mathField: mathField,
        keyboard: this._mathVirtualKeyboard,
        isOpen: () => this.keyboardOpen,
        setOpen: v => this.keyboardOpen = v,
        //updateLayout: () => this.updateMathFieldKeyboard(),
        pageContainerSelector: '.page-container',
        pageOriginalHeight: this.pageContainerDefaultHeight
      });
    });
  }
  private updateMathFieldIcon(): void {
    setTimeout(() => {
      const toolbarEl = this.elementRef.nativeElement.querySelector('.ql-mathField');
      if (toolbarEl) {
        toolbarEl.innerHTML = `
          <svg viewBox="0 0 18 18"> 
            <path class="ql-fill" d="M11.759,2.482a2.561,2.561,0,0,0-3.53.607A7.656,7.656,0,0,0,6.8,6.2C6.109,9.188,5.275,14.677,4.15,14.927a1.545,1.545,0,0,0-1.3-.933A0.922,0.922,0,0,0,2,15.036S1.954,16,4.119,16s3.091-2.691,3.7-5.553c0.177-.826.36-1.726,0.554-2.6L8.775,6.2c0.381-1.421.807-2.521,1.306-2.676a1.014,1.014,0,0,0,1.02.56A0.966,0.966,0,0,0,11.759,2.482Z"></path> 
            <rect class="ql-fill" height="1.6" rx="0.8" ry="0.8" width="5" x="5.15" y="6.2"></rect> 
            <path class="ql-fill" d="M13.663,12.027a1.662,1.662,0,0,1,.266-0.276q0.193,0.069.456,0.138a2.1,2.1,0,0,0,.535.069,1.075,1.075,0,0,0,.767-0.3,1.044,1.044,0,0,0,.314-0.8,0.84,0.84,0,0,0-.238-0.619,0.8,0.8,0,0,0-.594-0.239,1.154,1.154,0,0,0-.781.3,4.607,4.607,0,0,0-.781,1q-0.091.15-.218,0.346l-0.246.38c-0.068-.288-0.137-0.582-0.212-0.885-0.459-1.847-2.494-.984-2.941-0.8-0.482.2-.353,0.647-0.094,0.529a0.869,0.869,0,0,1,1.281.585c0.217,0.751.377,1.436,0.527,2.038a5.688,5.688,0,0,1-.362.467,2.69,2.69,0,0,1-.264.271q-0.221-.08-0.471-0.147a2.029,2.029,0,0,0-.522-0.066,1.079,1.079,0,0,0-.768.3A1.058,1.058,0,0,0,9,15.131a0.82,0.82,0,0,0,.832.852,1.134,1.134,0,0,0,.787-0.3,5.11,5.11,0,0,0,.776-0.993q0.141-.219.215-0.34c0.046-.076.122-0.194,0.223-0.346a2.786,2.786,0,0,0,.918,1.726,2.582,2.582,0,0,0,2.376-.185c0.317-.181.212-0.565,0-0.494A0.807,0.807,0,0,1,14.176,15a5.159,5.159,0,0,1-.913-2.446l0,0Q13.487,12.24,13.663,12.027Z"></path> 
          </svg>
        `;
        toolbarEl.setAttribute('title', 'Insert Equation');
      }
    });
  }

  private patchTooltipPositioning() {
    const Tooltip = Quill.import('ui/tooltip');
    const originalPosition = Tooltip.prototype.position;
    Tooltip.prototype.position = function (reference: { left: number; width: number; bottom: number }) {
      originalPosition.call(this, reference);

      // Then check and correct ONLY if left position is negative
      const currentLeft = parseInt(this.root.style.left);
      if (currentLeft < 0) {
        this.root.style.left = '0px';
      } else {
        this.root.style.left = `${currentLeft - 12}px`; // take care of margin
      }
    };
  }
  private insertMathField() {
    const range = this.quill.getSelection(true);
    if(this.keyboardOpen)
    {
      closeVirtualKeyboard({
        mathField: null,
        keyboard: this._mathVirtualKeyboard,
        isOpen: () => this.keyboardOpen,
        setOpen: v => this.keyboardOpen = v,
        pageContainerSelector: '.page-container',
        pageOriginalHeight: this.pageContainerDefaultHeight
      })
    }

    // Insert the input blot at current position
    this.quill.insertEmbed(range.index, 'input', {
      content: '',  // Initialize with empty content
      width: '150px',
      pageHeight: this.pageContainerDefaultHeight,
      keyboardOpen: this.keyboardOpen
    });
    // Insert a space after the math field
    this.quill.insertText(range.index + 1, ' ');

    // Move cursor after the inserted blot
    this.quill.setSelection(range.index, 1);

    //this.updateMathFieldKeyboard();
  }

  private sanitizeHTML(html) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    // Remove zero-width spaces typically added during mathfield blot insertion
    wrapper.innerHTML = wrapper.innerHTML.replace(/\u200B|\uFEFF/g, '');
    return wrapper.innerHTML;
  }
}